
import { useState, useRef, useEffect, useMemo } from "react";

import "./DatePicker.css";
import {useTranslation} from "../../i18n";

const LOCALE_MAP = {
  uk: "uk-UA",
  ru: "ru-RU",
  en: "en-US",
};

// Встроенные дни недели для каждого языка (Пн-Нд / Mo-Su)
const WEEKDAYS_BY_LANG = {
  uk: ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Нд"],
  ru: ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"],
  de: ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"],
  en: ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"],
};

function toDisplay(value) {
  if (!value) return "";
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return "";
  const [, year, month, day] = match;
  return `${day}.${month}.${year}`;
}

function toIso(year, month, day) {
  const mm = String(month + 1).padStart(2, "0");
  const dd = String(day).padStart(2, "0");
  return `${year}-${mm}-${dd}`;
}

function daysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function mondayFirstWeekday(year, month, day) {
  const jsDay = new Date(year, month, day).getDay(); // вс=0..сб=6
  return (jsDay + 6) % 7;
}

export function DatePicker({ value, onChange, placeholder }) {
  const { t, i18n, language } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Определение текущего языка (uk / ru / de / en)
  const currentLang = (i18n?.resolvedLanguage || i18n?.language || language || "uk").toLowerCase().slice(0, 2);
  const locale = LOCALE_MAP[currentLang] || "uk-UA";

  // Дни недели: сначала пробуем из i18n, если нет — берем из WEEKDAYS_BY_LANG
  const weekdays = useMemo(() => {
    const fromI18n = t("datePicker.weekdays", { returnObjects: true });
    if (Array.isArray(fromI18n)) return fromI18n;
    return WEEKDAYS_BY_LANG[currentLang] || WEEKDAYS_BY_LANG.uk;
  }, [t, currentLang]);

  const parsed = value ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(value) : null;
  const initialYear = parsed ? Number(parsed[1]) : new Date().getFullYear();
  const initialMonth = parsed ? Number(parsed[2]) - 1 : new Date().getMonth();

  const [viewYear, setViewYear] = useState(initialYear);
  const [viewMonth, setViewMonth] = useState(initialMonth);

  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const openPicker = () => {
    if (parsed) {
      setViewYear(Number(parsed[1]));
      setViewMonth(Number(parsed[2]) - 1);
    }
    setOpen(true);
  };

  const goPrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const goNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const selectDay = (day) => {
    onChange(toIso(viewYear, viewMonth, day));
    setOpen(false);
  };

  const clear = () => {
    onChange(null);
    setOpen(false);
  };

  const totalDays = daysInMonth(viewYear, viewMonth);
  const leadingBlanks = mondayFirstWeekday(viewYear, viewMonth, 1);
  const cells = [
    ...Array(leadingBlanks).fill(null),
    ...Array.from({ length: totalDays }, (_, i) => i + 1),
  ];

  // Название месяца и год (на языке текущей локали)
  const monthLabel = new Date(viewYear, viewMonth, 1).toLocaleDateString(locale, {
    month: "long",
    year: "numeric",
  });

  return (
    <div className="datepicker" ref={containerRef}>
      <input
        type="text"
        className="datepicker-input"
        value={toDisplay(value)}
        placeholder={placeholder || t("datePicker.placeholder", "ДД.ММ.РРРР")}
        readOnly
        onClick={openPicker}
      />
      {open && (
        <div className="datepicker-popup">
          <div className="datepicker-header">
            <button
              type="button"
              className="datepicker-nav"
              onClick={goPrevMonth}
              aria-label={t("datePicker.prevMonth", "Попередній місяць")}
            >
              ‹
            </button>
            <span className="datepicker-month-label">{monthLabel}</span>
            <button
              type="button"
              className="datepicker-nav"
              onClick={goNextMonth}
              aria-label={t("datePicker.nextMonth", "Наступний місяць")}
            >
              ›
            </button>
          </div>
          <div className="datepicker-weekdays">
            {weekdays.map((w, i) => (
              <span key={`${w}-${i}`}>{w}</span>
            ))}
          </div>
          <div className="datepicker-grid">
            {cells.map((day, i) => {
              if (day === null) {
                return <span key={`blank-${i}`} className="datepicker-cell datepicker-cell-blank" />;
              }
              const iso = toIso(viewYear, viewMonth, day);
              const isSelected = iso === value;
              return (
                <button
                  type="button"
                  key={iso}
                  className={`datepicker-cell ${isSelected ? "selected" : ""}`}
                  onClick={() => selectDay(day)}
                >
                  {day}
                </button>
              );
            })}
          </div>
          {value && (
            <button type="button" className="datepicker-clear" onClick={clear}>
              {t("common.clear", "Очистити")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default DatePicker;